// ===========================================================================
// Links in a message, made clickable -- carefully.
//
// A message is text a person typed, so nothing here ever becomes markup: the
// text is split into runs, and only a run that parses as an http(s) URL
// becomes an anchor, built as an element with its href set, never inserted
// as HTML. Two kinds of link come out, and they are treated differently:
//
//   * A LUMI LINK: a link to this site (a room, a person's 1:1 link, a
//     community, or a page). Always clickable, opens in the same tab, since
//     the app is what handles /r/CODE and there is only ever one chat at a
//     time. Marked so the styling can say "this stays on Lumi".
//   * An EXTERNAL link. Clickable only where the caller says so: in adult
//     chats, and in Lumi's own notices. In a teen chat an external link a
//     stranger posted stays plain text, exactly as it always was: the door
//     off the platform is the one a child should not be handed. It opens in
//     a new tab with no referrer and no window handle back to us.
//
// Trailing punctuation is not part of a link. "see https://x.y/z." links to
// https://x.y/z, and "(https://x.y)" to https://x.y.
//
// ASCII only.
// ===========================================================================

import { el } from '../dom.js';

/** Where Lumi lives, whatever origin this build happens to run on. */
export const LUMI_HOSTS = Object.freeze(['lumichat.io', 'www.lumichat.io']);

const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;
const TRAILING = /[.,;:!?'")\]}]+$/;

/** A URL with the punctuation that belongs to the sentence, not the link,
 *  taken off the end. A closing bracket is kept when the link opened one. */
/**
 * The URL if it is one a browser may be pointed at, else ''.
 *
 * http and https and nothing else. javascript:, data: and blob: are all
 * navigable from an href and all of them are somebody else's code running as
 * this page. splitLinks has always done this for the links inside a message;
 * it is exported because the link-preview card builds its href from a string
 * the server supplies rather than from the typed text, and skipped it.
 *
 * @param {string} raw
 * @returns {string}
 */
export function safeHref(raw) {
  try {
    const u = new URL(String(raw || ''));
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : '';
  } catch {
    return '';
  }
}

export function trimLink(raw) {
  let url = String(raw || '');
  for (;;) {
    const m = url.match(TRAILING);
    if (!m) break;
    const tail = m[0];
    // Keep one closing paren/bracket per unmatched opener inside the URL.
    const keepParen = tail.startsWith(')') && (url.split('(').length - 1) > (url.split(')').length - 1 - tail.split(')').length + 1);
    if (keepParen) break;
    url = url.slice(0, -tail.length);
    if (!url) break;
  }
  return url;
}

/** Is this link one of ours? Same origin as the page, or a Lumi host. */
export function isLumiLink(url, origin = typeof location !== 'undefined' ? location.origin : '') {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    if (origin && u.origin === origin) return true;
    return LUMI_HOSTS.includes(u.hostname.toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Split a message into runs of text and links.
 *
 * @returns {Array<{ type: 'text', value: string } | { type: 'link', url: string, lumi: boolean }>}
 */
export function splitLinks(text, origin) {
  const s = String(text || '');
  const out = [];
  let last = 0;
  URL_RE.lastIndex = 0;
  let m;
  while ((m = URL_RE.exec(s))) {
    const url = trimLink(m[0]);
    let ok = false;
    try {
      const u = new URL(url);
      ok = u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      ok = false;
    }
    if (!ok) continue;
    if (m.index > last) out.push({ type: 'text', value: s.slice(last, m.index) });
    out.push({ type: 'link', url, lumi: isLumiLink(url, origin) });
    last = m.index + url.length;
  }
  if (last < s.length) out.push({ type: 'text', value: s.slice(last) });
  return out;
}

/**
 * The message as DOM children: strings for text, anchors for the links that
 * may be clicked here. A link that may not be clicked is left as its text.
 *
 * @param {string} text
 * @param {{ external?: boolean }} [opts]  whether links off Lumi are clickable
 */
export function linkifyNodes(text, { external = false } = {}) {
  return splitLinks(text).map((run) => {
    if (run.type === 'text') return run.value;
    if (run.lumi) return el('a', { class: 'msg-link lumi-link', href: run.url, text: run.url });
    if (!external) return run.url;
    return el('a', { class: 'msg-link', href: run.url, target: '_blank', rel: 'noopener noreferrer', text: run.url });
  });
}

export default { LUMI_HOSTS, trimLink, isLumiLink, splitLinks, linkifyNodes };
